import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  getBezierPath,
  BaseEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ArrowLeft, Save, Zap, GitBranch, Send, Mail, Sparkles, Plus, Target, X, Users, CheckSquare, Tag, ChevronDown, ChevronRight, Search } from 'lucide-react';
import api from '../utils/api';
import TriggerNode from './workflow-nodes/TriggerNode';
import ConditionNode from './workflow-nodes/ConditionNode';
import IfElseNode from './workflow-nodes/IfElseNode';
import ActionNode from './workflow-nodes/ActionNode';
import NodeConfigModal from './workflow-nodes/NodeConfigModal';

// Custom edge with delete button
const DeletableEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, markerEnd, data }) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const onEdgeDelete = (evt) => {
    evt.stopPropagation();
    if (data?.onDelete) {
      data.onDelete(id);
    }
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <foreignObject
        width={24}
        height={24}
        x={labelX - 12}
        y={labelY - 12}
        className="edgebutton-foreignobject"
        requiredExtensions="http://www.w3.org/1999/xhtml"
      >
        <div className="flex items-center justify-center w-full h-full">
          <button
            className="w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white shadow-md transition-all opacity-70 hover:opacity-100"
            onClick={onEdgeDelete}
            title="Delete connection"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </foreignObject>
    </>
  );
};

const edgeTypes = {
  deletable: DeletableEdge,
};

const nodeTypes = {
  trigger: TriggerNode,
  condition: ConditionNode,
  ifelse: IfElseNode,
  action: ActionNode,
};

const WorkflowTemplateBuilder = ({ template, onClose, onSave }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [triggerType, setTriggerType] = useState('task_stage_changed');
  const [triggerConfig, setTriggerConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [showNodePalette, setShowNodePalette] = useState(true);
  const [selectedNodeForConfig, setSelectedNodeForConfig] = useState(null);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    triggers: true,
    logic: true,
    actions: true
  });
  const [actionSearch, setActionSearch] = useState('');

  // Load existing template or reset for new
  useEffect(() => {
    if (template) {
      console.log('📂 Loading existing template:', template.id, template.name);
      setTemplateName(template.name || '');
      setTemplateDescription(template.description || '');
      setTriggerType(template.trigger_type || 'task_stage_changed');
      setTriggerConfig(template.trigger_config || {});
      
      if (template.nodes && Array.isArray(template.nodes)) {
        // Store nodes initially - callbacks will be attached in separate useEffect
        setNodes(template.nodes);
      } else {
        setNodes([]);
      }
      if (template.edges && Array.isArray(template.edges)) {
        setEdges(template.edges);
      } else {
        setEdges([]);
      }
    } else {
      // Reset for new template
      console.log('📝 Creating new template - resetting state');
      setTemplateName('');
      setTemplateDescription('');
      setTriggerType('task_stage_changed');
      setTriggerConfig({});
      setNodes([]);
      setEdges([]);
    }
  }, [template]);

  const handleNodeConfig = (nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setSelectedNodeForConfig(node);
    }
  };

  const handleSaveNodeConfig = (updatedNode) => {
    // Create the final node with callbacks attached
    const finalNode = {
      ...updatedNode,
      data: {
        ...updatedNode.data,
        onConfigClick: () => handleNodeConfig(updatedNode.id),
        onDelete: () => handleDeleteNode(updatedNode.id),
        onCopy: () => handleCopyNode(updatedNode.id)
      }
    };
    
    // Update nodes array
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === updatedNode.id) {
          return finalNode;
        }
        return node;
      })
    );
    
    // Close the modal after save in template builder
    setSelectedNodeForConfig(null);
  };

  // Handle double-click on node
  const onNodeDoubleClick = useCallback((event, node) => {
    handleNodeConfig(node.id);
  }, [nodes]);

  // Handle node deletion
  const handleDeleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
  };

  // Handle node copy
  const handleCopyNode = (nodeId) => {
    const nodeToCopy = nodes.find(n => n.id === nodeId);
    if (!nodeToCopy) return;

    // Deep-clone node settings and strip callback functions so all config is preserved
    const { onConfigClick, onDelete, onCopy, ...sourceData } = nodeToCopy.data || {};
    let copiedData;
    try {
      copiedData = (typeof structuredClone === 'function')
        ? structuredClone(sourceData)
        : JSON.parse(JSON.stringify(sourceData));
    } catch {
      copiedData = { ...sourceData };
    }

    const newNodeId = `${nodeToCopy.type}-${Date.now()}`;
    const newNode = {
      id: newNodeId,
      type: nodeToCopy.type,
      position: {
        x: nodeToCopy.position.x + 50,
        y: nodeToCopy.position.y + 50
      },
      data: {
        ...copiedData,
        label: `${copiedData.label || nodeToCopy.type} (Copy)`,
        onConfigClick: () => handleNodeConfig(newNodeId),
        onDelete: () => handleDeleteNode(newNodeId),
        onCopy: () => handleCopyNode(newNodeId)
      },
    };

    setNodes((nds) => nds.concat(newNode));
  };

  // Ensure all nodes have callbacks attached (important for loaded templates)
  useEffect(() => {
    setNodes((currentNodes) => {
      // Check if any node is missing callbacks
      const needsUpdate = currentNodes.some(node => !node.data?.onDelete);
      if (!needsUpdate || currentNodes.length === 0) return currentNodes;
      
      console.log('🔧 Attaching callbacks to loaded template nodes');
      return currentNodes.map(node => {
        // Skip if callbacks already exist
        if (node.data?.onDelete) return node;
        
        return {
          ...node,
          data: {
            ...node.data,
            onConfigClick: () => handleNodeConfig(node.id),
            onDelete: () => handleDeleteNode(node.id),
            onCopy: () => handleCopyNode(node.id)
          }
        };
      });
    });
  }, [nodes.length]); // Only run when nodes array length changes (e.g., after load)

  // Ensure all edges have delete handler and correct type (important for loaded templates)
  useEffect(() => {
    setEdges((currentEdges) => {
      const needsUpdate = currentEdges.some(edge => !edge.data?.onDelete || edge.type !== 'deletable');
      if (!needsUpdate || currentEdges.length === 0) return currentEdges;
      
      console.log('🔧 Attaching delete handler to loaded template edges');
      return currentEdges.map(edge => ({
        ...edge,
        type: 'deletable',
        style: { stroke: '#9333ea', strokeWidth: 2 },
        data: { 
          ...edge.data,
          onDelete: (edgeId) => setEdges((eds) => eds.filter((e) => e.id !== edgeId))
        }
      }));
    });
  }, [edges.length, setEdges]);

  // Handle key press for delete
  const onNodesDelete = useCallback((deleted) => {
    setEdges((eds) =>
      eds.filter((edge) => 
        !deleted.some((node) => node.id === edge.source || node.id === edge.target)
      )
    );
  }, [setEdges]);


  // Handle edge deletion
  const handleDeleteEdge = useCallback((edgeId) => {
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
  }, [setEdges]);

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'deletable',
            animated: true,
            markerEnd: {
              type: MarkerType.ArrowClosed,
            },
            style: { stroke: '#9333ea', strokeWidth: 2 },
            data: { onDelete: handleDeleteEdge },
          },
          eds
        )
      ),
    [setEdges, handleDeleteEdge]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      const nodeDataStr = event.dataTransfer.getData('application/nodedata');
      
      if (typeof type === 'undefined' || !type) {
        return;
      }

      let nodeData = {};
      try {
        nodeData = JSON.parse(nodeDataStr);
      } catch {
        nodeData = { label: type };
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNodeId = `${type}-${Date.now()}`;
      const newNode = {
        id: newNodeId,
        type,
        position,
        data: {
          ...nodeData,
          onConfigClick: () => handleNodeConfig(newNodeId),
          onDelete: () => handleDeleteNode(newNodeId),
          onCopy: () => handleCopyNode(newNodeId)
        },
      };

      setNodes((nds) => nds.concat(newNode));

      // If it's a trigger node, update the workflow trigger type
      if (type === 'trigger' && nodeData.triggerType) {
        setTriggerType(nodeData.triggerType);
      }
    },
    [reactFlowInstance, setNodes]
  );

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    if (nodes.length === 0) {
      alert('Please add at least one node to the workflow');
      return;
    }

    setSaving(true);
    try {
      // Clean nodes (remove callback functions before saving)
      const cleanNodes = nodes.map(node => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          ...node.data,
          // Remove function references
          onConfigClick: undefined,
          onDelete: undefined,
          onCopy: undefined
        }
      }));

      // Remove undefined keys
      cleanNodes.forEach(node => {
        Object.keys(node.data).forEach(key => {
          if (node.data[key] === undefined) {
            delete node.data[key];
          }
        });
      });

      // Ensure trigger_config is an object
      let cleanTriggerConfig = triggerConfig;
      if (typeof triggerConfig === 'string') {
        try {
          cleanTriggerConfig = JSON.parse(triggerConfig);
        } catch {
          cleanTriggerConfig = {};
        }
      }
      if (!cleanTriggerConfig || typeof cleanTriggerConfig !== 'object') {
        cleanTriggerConfig = {};
      }
      
      const templateData = {
        name: templateName,
        description: templateDescription,
        trigger_type: triggerType,
        trigger_config: cleanTriggerConfig,
        nodes: cleanNodes,
        edges: edges,
      };

      console.log('Saving template data:', JSON.stringify(templateData, null, 2));

      const token = localStorage.getItem('super_admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      if (template?.id) {
        // Update existing
        await api.put(`/super-admin/template-library/workflows/${template.id}`, templateData, { headers });
      } else {
        // Create new
        await api.post('/super-admin/template-library/workflows', templateData, { headers });
      }

      alert('Workflow template saved successfully!');
      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error('Error saving template:', err);
      alert(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const onDragStart = (event, nodeType, nodeData) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('application/nodedata', JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 z-[60] flex items-center justify-center">
      <div className="bg-gray-50 w-[95vw] h-[95vh] rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0 max-w-2xl">
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Template Name"
                  className="text-2xl font-bold text-gray-900 border-none outline-none bg-transparent w-full truncate"
                />
                <input
                  type="text"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Add description..."
                  className="text-sm text-gray-600 border-none outline-none bg-transparent w-full"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 bg-purple-100 px-3 py-1 rounded-full">
                Super Admin Template
              </span>
              <button
                onClick={saveTemplate}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Node Palette */}
          {showNodePalette && (
            <div className="w-72 bg-white border-r border-gray-200 p-4 overflow-y-auto">
              <h3 className="font-semibold text-gray-900 mb-4">Add Nodes</h3>
              
              {/* Search for All Nodes */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search nodes..."
                    value={actionSearch}
                    onChange={(e) => setActionSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  {actionSearch && (
                    <button
                      onClick={() => setActionSearch('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Trigger Nodes */}
              <div className="mb-4">
                <button
                  onClick={() => setExpandedSections(prev => ({ ...prev, triggers: !prev.triggers }))}
                  className="flex items-center justify-between w-full text-left mb-2 hover:bg-gray-50 rounded p-1 -ml-1"
                >
                  <h4 className="text-xs font-semibold text-gray-500 uppercase">Triggers</h4>
                  {expandedSections.triggers ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                {expandedSections.triggers && (
                <div className="space-y-2">
                  {[
                    { label: 'Stage Changed', triggerType: 'task_stage_changed' },
                    { label: 'Task Created', triggerType: 'task_created' },
                    { label: 'Project Created', triggerType: 'project_created' },
                    { label: 'Client Created', triggerType: 'client_created' },
                    { label: 'Category Changed', triggerType: 'category_changed' },
                    { label: 'Task Completed', triggerType: 'task_completed' },
                    { label: 'Task Assigned', triggerType: 'task_assigned' },
                    { label: 'Priority Changed', triggerType: 'priority_changed' },
                    { label: 'Task Due Soon', triggerType: 'task_due_soon' },
                    { label: 'Task Overdue', triggerType: 'task_overdue' },
                    { label: 'Comment Added', triggerType: 'comment_added' },
                    { label: 'Portal Submission', triggerType: 'client_portal_submission' },
                    { label: 'Portal Access Granted', triggerType: 'portal_access_granted' },
                    { label: 'Inbound Webhook', triggerType: 'inbound_webhook' },
                  ]
                    .filter(trigger => !actionSearch || trigger.label.toLowerCase().includes(actionSearch.toLowerCase()))
                    .map(trigger => (
                      <div
                        key={trigger.triggerType}
                        className="p-3 bg-green-50 border border-green-200 rounded-lg cursor-move hover:bg-green-100 transition-colors"
                        draggable
                        onDragStart={(e) => onDragStart(e, 'trigger', { label: trigger.label, triggerType: trigger.triggerType })}
                      >
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-gray-900">{trigger.label}</span>
                        </div>
                      </div>
                    ))}
                </div>
                )}
              </div>

              {/* Logic / Branching */}
              <div className="mb-4">
                <button
                  onClick={() => setExpandedSections(prev => ({ ...prev, logic: !prev.logic }))}
                  className="flex items-center justify-between w-full text-left mb-2 hover:bg-gray-50 rounded p-1 -ml-1"
                >
                  <h4 className="text-xs font-semibold text-gray-500 uppercase">Logic</h4>
                  {expandedSections.logic ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                {expandedSections.logic && (
                <div className="space-y-2">
                  {(!actionSearch || 'if else branch conditional'.includes(actionSearch.toLowerCase()) || 'if / else'.toLowerCase().includes(actionSearch.toLowerCase())) && (
                  <div
                    className="p-3 bg-purple-50 border border-purple-200 rounded-lg cursor-move hover:bg-purple-100 transition-colors"
                    draggable
                    onDragStart={(e) => onDragStart(e, 'ifelse', { 
                      label: 'If / Else', 
                      branches: [{ id: 'branch-1', name: 'Branch 1', conditionGroups: [] }],
                      elseEnabled: true
                    })}
                  >
                    <div className="flex items-center gap-2">
                      <GitBranch className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-gray-900">If / Else</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Multi-branch conditional with AND/OR logic</p>
                  </div>
                  )}
                </div>
                )}
              </div>

              {/* Action Nodes */}
              <div className="mb-6">
                <button
                  onClick={() => setExpandedSections(prev => ({ ...prev, actions: !prev.actions }))}
                  className="flex items-center justify-between w-full text-left mb-2 hover:bg-gray-50 rounded p-1 -ml-1"
                >
                  <h4 className="text-xs font-semibold text-gray-500 uppercase">Actions</h4>
                  {expandedSections.actions ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                {expandedSections.actions && (
                <div className="space-y-2">
                  {[
                    { label: 'Send Webhook', actionType: 'webhook', icon: Send, color: 'blue' },
                    { label: 'Send Email', actionType: 'email', icon: Mail, color: 'blue' },
                    { label: 'Send Portal Invite', actionType: 'send_portal_invite', icon: Mail, color: 'green' },
                    { label: 'Grant Portal Access', actionType: 'grant_portal_access', icon: Users, color: 'green' },
                    { label: 'AI Task Summary', actionType: 'ai_generate', icon: Sparkles, color: 'blue' },
                    { label: 'GPT Action', actionType: 'gpt_action', icon: Sparkles, color: 'blue' },
                    { label: 'Wait', actionType: 'wait', icon: Target, color: 'amber' },
                    { label: 'Go To', actionType: 'go_to', icon: Target, color: 'purple' },
                    { label: 'Add to Workflow', actionType: 'add_to_workflow', icon: Zap, color: 'green' },
                    { label: 'Remove from Workflow', actionType: 'remove_from_workflow', icon: Zap, color: 'red' },
                    { label: 'Update Assignees', actionType: 'update_assignees', icon: Target, color: 'blue' },
                    { label: 'Change Category', actionType: 'change_category', icon: Tag, color: 'blue' },
                    { label: 'Update Project Manager', actionType: 'update_project_manager', icon: Users, color: 'blue' },
                    { label: 'Create Task', actionType: 'create_task', icon: CheckSquare, color: 'blue' },
                    { label: 'Add Subtask', actionType: 'add_subtask', icon: CheckSquare, color: 'green' },
                    { label: 'Create Project', actionType: 'create_project', icon: Plus, color: 'blue' },
                    { label: 'Create Client', actionType: 'create_client', icon: Users, color: 'green' },
                    { label: 'Archive Task', actionType: 'archive_task', icon: Target, color: 'amber' },
                    { label: 'Update Priority', actionType: 'update_priority', icon: Target, color: 'blue' },
                    { label: 'Move to Stage', actionType: 'move_to_stage', icon: Target, color: 'blue' },
                    { label: 'Change Project', actionType: 'change_project', icon: Target, color: 'purple' },
                    { label: 'Set Due Date', actionType: 'set_due_date', icon: Target, color: 'blue' },
                    { label: 'Add Comment', actionType: 'add_comment', icon: Target, color: 'blue' },
                    { label: 'Send Notification', actionType: 'send_notification', icon: Send, color: 'blue' },
                    { label: 'Add Tag', actionType: 'add_tag', icon: Tag, color: 'green' },
                    { label: 'Remove Tag', actionType: 'remove_tag', icon: Tag, color: 'red' },
                  ]
                    .filter(action => !actionSearch || action.label.toLowerCase().includes(actionSearch.toLowerCase()))
                    .map(action => {
                      const Icon = action.icon || Target;
                      const bgColor = {
                        blue: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
                        green: 'bg-green-50 border-green-200 hover:bg-green-100',
                        amber: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
                        purple: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
                        red: 'bg-red-50 border-red-200 hover:bg-red-100',
                      }[action.color] || 'bg-blue-50 border-blue-200 hover:bg-blue-100';
                      const iconColor = {
                        blue: 'text-blue-600',
                        green: 'text-green-600',
                        amber: 'text-amber-600',
                        purple: 'text-purple-600',
                        red: 'text-red-600',
                      }[action.color] || 'text-blue-600';
                      
                      return (
                        <div
                          key={action.actionType}
                          className={`p-3 border rounded-lg cursor-move transition-colors ${bgColor}`}
                          draggable
                          onDragStart={(e) => onDragStart(e, 'action', { label: action.label, actionType: action.actionType })}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${iconColor}`} />
                            <span className="text-sm font-medium text-gray-900">{action.label}</span>
                          </div>
                        </div>
                      );
                    })}
                </div>
                )}
              </div>
            </div>
          )}

          {/* React Flow Canvas */}
          <div className="flex-1" ref={reactFlowWrapper}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={(instance) => {
                setReactFlowInstance(instance);
                // Focus on the first node (trigger) when opening an existing template
                if (template?.nodes?.length > 0) {
                  setTimeout(() => {
                    // Find the trigger node or first node
                    const triggerNode = template.nodes.find(n => n.type === 'trigger') || template.nodes[0];
                    if (triggerNode?.position) {
                      // Set viewport so the first node is near the top of the canvas
                      instance.setViewport({
                        x: -triggerNode.position.x + 300,
                        y: -triggerNode.position.y + 80,
                        zoom: 1
                      }, { duration: 300 });
                    } else {
                      instance.fitView({ padding: 0.2 });
                    }
                  }, 100);
                }
              }}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onNodeDoubleClick={onNodeDoubleClick}
              onNodesDelete={onNodesDelete}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              defaultViewport={{ x: 0, y: 0, zoom: 1 }}
              deleteKeyCode="Delete"
            >
              <Controls />
              <MiniMap />
              <Background variant="dots" gap={12} size={1} />
            </ReactFlow>
          </div>
        </div>

        {/* Node Configuration Modal */}
        {selectedNodeForConfig && (
          <NodeConfigModal
            node={selectedNodeForConfig}
            workflowId={null}
            allNodes={nodes}
            webhookPayloadVariables={[]}
            onWebhookVariablesChange={() => {}}
            onClose={() => setSelectedNodeForConfig(null)}
            onSave={handleSaveNodeConfig}
            onSaveWorkflow={null}
          />
        )}
      </div>
    </div>
  );
};

export default WorkflowTemplateBuilder;
